import { CHANNEL } from '../../../constants/channel.constants';
import { ExternalRequest } from '../../../models/channel-communicator/external-request.model';
import { ExternalResponse } from '../../../models/channel-communicator/external-response.model';
import { BytteService } from '../../external/bytte/bytte.service';
import { CyxteraService } from '../../external/cyxtera/cyxtera.service';

declare function openPage(url: string, indicator: number): any;
declare function openExternalBrowser(url: string, indicator: number): any;
declare function clearCache(): any;
/**
 * Servicio base de comunicación con el canal, clase intermediaria
 *
 * *Base communicator channel, intermediate class*
 *
 * @type {abstract class} BaseChannelCommunicatorService
 */
export abstract class BaseChannelCommunicatorService {
  /**
   * Variable de origen de abre cuentas
   *
   * *Abre cuentas variable origin*
   *
   * @access private
   * @type {string} ABRE_CUENTAS_ORIGIN
   */
  private ABRE_CUENTAS_ORIGIN: string;

  /**
   * Servicio para el escaneo de imagenes
   *
   * *Scanning image servicce*
   *
   * @access private
   * @type {BytteService} bytteService
   */
  private bytteService: BytteService;

  /**
   * Servicio de cyxtera
   *
   * *Cyxtera service*
   *
   * @access {private}
   * @type {CyxteraService} cyxteraService
   */
  private cyxteraService: CyxteraService;

  private is_portal = false;
  /**
   * Inicializa el servicio para la comunicación con el canal
   *
   * *Initializes the service that communicates with the channel*
   *
   * @type {function}
   */
  initService(portalPN?: boolean) {
    this.ABRE_CUENTAS_ORIGIN = CHANNEL.ABRECUENTAS.NAME;
    this.setPortal(true);
    // tslint:disable-next-line:early-exit
    if (!portalPN) {
      this.setPortal(false);
      this.bytteService = new BytteService(this.ABRE_CUENTAS_ORIGIN, this.createPromise, this.postMessageToChannel);
      this.cyxteraService = new CyxteraService(this.ABRE_CUENTAS_ORIGIN, this.createPromise, this.postMessageToChannel);
    }
  }

  setPortal(portalState) {
    this.is_portal = portalState;
  }

  getPortal() {
    return this.is_portal;
  }

  /**
   * Creación de la promesa
   *
   * *Promise creation*
   *
   * @param {string} op
   * @type {function}
   * @type {Promise<ExternalResponse>}
   */
  abstract createPromise(op: string): Promise<ExternalResponse>;

  /**
   * Mensaje para el canal mediante la solicitud post
   *
   * *Message to channel by post method*
   *
   * @type {function}
   *
   * @param {ExternalRequest} request
   */
  abstract postMessageToChannel(request: ExternalRequest);

  /**
   * Registra la licencia bytte para el escaneo de imágenes
   *
   * *Bytte license registration for images scanning*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseRegistrationUrl
   * @returns {Promise}
   */
  registerBytteLicense(licenseRegistrationUrl: string): Promise<any> {
    return this.bytteService.registerBytteLicense(licenseRegistrationUrl);
  }

  /**
   * Función que retorna el servicio para escanear la parte de atrás del documento
   *
   * *Function that returns back document scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  backDocumentScan(licenseAndroid: string, licenseIos: string, password: string, timeout: number): Promise<any> {
    return this.bytteService.backDocumentScan(licenseAndroid, licenseIos, password, timeout);
  }

  /**
   * Función que retorna el servicio para escanear la parte de adelante del documento
   *
   * *Function that returns front document scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  frontDocumentScan(licenseAndroid: string, licenseIos: string, password: string, timeout: number): Promise<any> {
    return this.bytteService.frontDocumentScan(licenseAndroid, licenseIos, password, timeout);
  }

  /**
   * Función que retorna el servicio para escanear las huellas digitales del usuario
   *
   * *Function that returns user's fringer print scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  fingerPrintsScan(fingerPrintSigned: string, password: string, timeout: number): Promise<any> {
    return this.bytteService.fingerPrintsScan(fingerPrintSigned, password, timeout);
  }

  /**
   * Función que limpia los archivos residuales
   *
   * *Function cleans residual files*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  cleanResidualFiles(): Promise<any> {
    return this.bytteService.cleanResidualFiles();
  }

  /**
   * Función que inicializa las licencias Dsb
   *
   * *function that initializes Dsb licenses*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} license
   * @returns {Promise}
   */
  initDsbLicense(license: string): Promise<any> {
    return this.cyxteraService.initDsbLicense(license);
  }

  /**
   * Función que valida la seguridad por las reglas de riesgo
   *
   * *Function that validates security by risk rules*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  isSecureByRiskRules(): Promise<any> {
    return this.cyxteraService.isSecureByRiskRules();
  }

  /**
   * Función que valida la seguridad del certificado
   *
   * *Function that validates certificate security*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} secureUrl
   * @returns {Promise}
   */
  isSecureCertificate(secureUrl: string): Promise<any> {
    return this.cyxteraService.isSecureCertificate(secureUrl);
  }

  /**
   * Función que envía la información a Dsb
   *
   * *Function that sends data to Dsb*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} userId
   * @returns {Promise}
   */
  sendDataToDSB(userId: string): Promise<any> {
    return this.cyxteraService.sendDataToDSB(userId);
  }

  /**
   * Función que redirige a la pantalla de login
   *
   * *function that redirect to login screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  redirectToLoginView(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'goToLoginPage',
      syncMode: false,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que redirige a la pantalla de lista de agencias
   *
   * *function that redirect to agency lists screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  redirectToAgencyListView(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'goToAgenciesPage',
      syncMode: false,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que redirige a la pantalla para abrir el archivo en pdf
   *
   * *function that redirect to open pdf file*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  openPdf(url: string): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'openPdf',
      syncMode: true,
      payload: {
        url,
      },
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que obtiene la información del dispositivo
   *
   * *function that obtains device information*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getDeviceInfo(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'getDeviceInfo',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que obtiene la información de la aplicación
   *
   * *function that obtains app information*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getAppInfo(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'getAppProperties',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que verifica y solicita los permisos
   *
   * *Function that verify and request permissions*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  verifyAndRequestPermissions(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'verifyAndRequestPermissions',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que redirige a la pantalla de configuración del dispositivo
   *
   * *Function that redirects to settings device screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  goToSettingsDevice(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'goToSettingsDevice',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que trae la informacion del cliente para post-login
   *
   * *Function get info user for post-login*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getInfoClient(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'getUserInfo',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que trae la token desde portal
   *
   * *Function get token from portal*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getObjectDataPortal(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'getObjectData',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que trae la data de la vitrina desde portal
   *
   * *Function get data offer from portal*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getFirmlyApprovedPortal(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'getFirmlyApproved',
      syncMode: true,
      payload: {},
    };

    promise = this.createPromise(request.op);

    this.postMessageToChannel(request);
    return promise;
  }

  /**
   * Función que redirige a la pantalla para abrir una pagina externa
   *
   * *function that redirect to open outside page*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  openPage(url: string, indicator: number, isPostLogin: boolean): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'openPage',
      syncMode: false,
      payload: {
        url,
        indicator,
      },
    };

    promise = this.createPromise(request.op);
    if (isPostLogin && !this.getPortal()) {
      openPage(url, indicator);
    } else {
      this.postMessageToChannel(request);
    }
    return promise;
  }

  /**
   * Función que redirige a la pantalla para abrir una pagina externa
   *
   * *function that redirect to open outside page*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  openExternalBrowser(url: string, indicator: number, isPostLogin: boolean): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'openExternalBrowser',
      syncMode: false,
      payload: {
        url,
        indicator,
      },
    };

    promise = this.createPromise(request.op);
    if (isPostLogin) {
      openExternalBrowser(url, indicator);
    } else {
      this.postMessageToChannel(request);
    }
    return promise;
  }

  clearCache(): Promise<ExternalResponse> {
    let promise: Promise<ExternalResponse>;

    const request: ExternalRequest = {
      origin: this.ABRE_CUENTAS_ORIGIN,
      op: 'clearCache',
      syncMode: false,
      payload: {},
    };

    promise = this.createPromise(request.op);
    clearCache();
    return promise;
  }

}
